import type { KnownEvent } from './analytics/events';

export const FONT_PROBE_EVENT = 'site.font_probe.detected' satisfies KnownEvent;

/** Width in px of `text` drawn in the CSS `font` shorthand `font`. */
export type Measure = (font: string, text: string) => number;

export type FamilyResult = {
  family: string;
  resolved: boolean;
  tofu: boolean;
  width_i: number;
  width_w: number;
};

export type FontDiagnosis = {
  families: FamilyResult[];
  tofu_families: string[];
};

const SIZE = '16px';
const NARROW = 'i'.repeat(10);
const WIDE = 'W'.repeat(10);
const SAMPLE = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
// Notdef boxes share one advance, so i and W converge; in Arial i is ~0.24 of W.
const TOFU_RATIO = 0.9;
const GENERICS = new Set(['serif', 'sans-serif', 'monospace', 'system-ui', 'ui-sans-serif', 'ui-serif', 'ui-monospace']);

/** Splits a computed `font-family` value into its entries, keeping CSS quoting. */
export function parseFontStack(value: string): string[] {
  const entries = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return entries.filter((e, i) => entries.indexOf(e) === i);
}

function isTofu(widthI: number, widthW: number): boolean {
  return widthW === 0 || widthI / widthW > TOFU_RATIO;
}

function probeFamily(family: string, measure: Measure): FamilyResult {
  const resolved =
    GENERICS.has(family) ||
    measure(`${SIZE} ${family}, monospace`, SAMPLE) !== measure(`${SIZE} monospace`, SAMPLE) ||
    measure(`${SIZE} ${family}, serif`, SAMPLE) !== measure(`${SIZE} serif`, SAMPLE);
  // serif tail: glyphs the family lacks fall back to real serif glyphs, not tofu
  const widthI = measure(`${SIZE} ${family}, serif`, NARROW);
  const widthW = measure(`${SIZE} ${family}, serif`, WIDE);
  return { family, resolved, tofu: resolved && isTofu(widthI, widthW), width_i: widthI, width_w: widthW };
}

/** Two measurements; the only cost a healthy page pays. */
export function stackRendersTofu(stack: string[], measure: Measure): boolean {
  const whole = `${SIZE} ${stack.join(', ')}`;
  return isTofu(measure(whole, NARROW), measure(whole, WIDE));
}

/** Probes each of `hostFamilies` alone; naming a webfont on a canvas would download it. */
export function diagnoseFonts(hostFamilies: string[], measure: Measure): FontDiagnosis {
  const families = hostFamilies.map((family) => probeFamily(family, measure));
  return { families, tofu_families: families.filter((f) => f.tofu).map((f) => f.family) };
}

type UADataLike = {
  getHighEntropyValues(hints: string[]): Promise<Record<string, unknown>>;
};

async function highEntropyUA(): Promise<Record<string, unknown>> {
  const uaData = (navigator as Navigator & { userAgentData?: UADataLike }).userAgentData;
  if (!uaData) return {};
  const values = await uaData.getHighEntropyValues(['fullVersionList', 'platformVersion']);
  return { ua_full_version_list: values.fullVersionList, ua_platform_version: values.platformVersion };
}

const unquote = (s: string) => s.replace(/^["']|["']$/g, '');

// Snapshot before measuring: unloaded means page text never reached the face.
function webfontStatuses(stack: string[]): { family: string; status: FontFaceLoadStatus }[] {
  const names = stack.map(unquote);
  const statuses: { family: string; status: FontFaceLoadStatus }[] = [];
  document.fonts.forEach((face) => {
    const family = unquote(face.family);
    if (names.includes(family)) statuses.push({ family, status: face.status });
  });
  return statuses;
}

function canvasMeasure(): Measure | null {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  return (font, text) => {
    ctx.font = font;
    return ctx.measureText(text).width;
  };
}

async function probe(capture: (props: Record<string, unknown>) => void): Promise<void> {
  const measure = canvasMeasure();
  if (!measure) return;
  const stack = parseFontStack(getComputedStyle(document.body).fontFamily);
  const webfonts = webfontStatuses(stack);
  const stackTofu = stackRendersTofu(stack, measure);
  const forced = new URLSearchParams(window.location.search).has('font_debug');
  // Per-family lookups cost ~19ms on a cold renderer, so only pay them when needed
  if (!stackTofu && !forced) return;
  const webfontNames = webfonts.map((w) => w.family);
  const hostFamilies = stack.filter((f) => !webfontNames.includes(unquote(f)));
  const diagnosis = diagnoseFonts(hostFamilies, measure);
  if (forced) console.table(diagnosis.families);
  capture({
    ...diagnosis,
    stack_tofu: stackTofu,
    forced,
    stack: stack.join(', '),
    webfonts,
    ...(await highEntropyUA()),
    user_agent: navigator.userAgent,
    path: window.location.pathname,
  });
}

/** Runs once when idle; silent unless text renders as notdef boxes or `?font_debug` is set. */
export function scheduleFontProbe(capture: (props: Record<string, unknown>) => void): void {
  if (typeof window === 'undefined') return;
  const run = () => void probe(capture).catch(() => {});
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 5000 });
  else setTimeout(run, 2000);
}
